import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { api } from '../lib/api';
import { Send, Bot, Check, X } from 'lucide-react';

export default function Assistant() {
  const { token, user } = useAuth();
  const { sessionId, messages, sending, addMessage, setSending, updateActionStatus } = useChat();
  const [input, setInput] = useState('');
  const listRef = useRef(null);
  const initials = user?.name?.split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';

  const scrollBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => { scrollBottom(); }, [messages, scrollBottom]);

  const sendMessage = async () => {
    if (!token || !input.trim() || sending) return;
    const text = input.trim();
    addMessage({ id: 'u-' + Date.now(), role: 'user', content: text });
    setInput('');
    setSending(true);
    try {
      const res = await api.aiChat(token, { message: text, session_id: sessionId, source: 'text' });
      addMessage({
        id: 'a-' + Date.now(),
        role: 'assistant',
        content: res.reply,
        action_request_id: res.action_request_id ?? null,
        action_preview: res.action_preview ?? null,
        action_status: res.action_request_id ? 'pending' : undefined,
      });
    } catch (e) {
      addMessage({ id: 'e-' + Date.now(), role: 'assistant', content: `Error: ${e?.message ?? 'AI failed'}` });
    } finally {
      setSending(false);
    }
  };

  const approve = async (msgId, actionId) => {
    if (!token) return;
    try {
      const res = await api.aiApprove(token, actionId);
      updateActionStatus(msgId, 'approved', res);
    } catch (e) { /* swallow */ }
  };

  const reject = async (msgId, actionId) => {
    if (!token) return;
    try {
      await api.aiReject(token, actionId);
      updateActionStatus(msgId, 'rejected');
    } catch (e) { /* swallow */ }
  };

  return (
    <div className="flex flex-col h-full" data-testid="assistant-page">
      {/* Messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-6 lg:px-12 py-6 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-20 h-20 rounded-full bg-brand-purple/10 flex items-center justify-center mb-4">
              <Bot size={40} className="text-brand-purple/40" />
            </div>
            <h2 className="text-white/60 text-lg font-bold">AI Business Concierge</h2>
            <p className="text-white/30 text-sm mt-2 max-w-sm">
              Ask me anything about your business — sales trends, inventory, expenses, customer insights, and more.
            </p>
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-3`}>
            {msg.role === 'assistant' && (
              <div className="w-8 h-8 rounded-full bg-brand-purple/15 flex items-center justify-center flex-shrink-0 mt-1">
                <Bot size={16} className="text-brand-purple" />
              </div>
            )}
            <div className="max-w-[70%]">
              <div
                data-testid={`chat-msg-${msg.id}`}
                className={`px-5 py-3 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-br from-brand-purple to-brand-purple-deep text-white rounded-br-md shadow-accent'
                    : 'bg-white/8 text-white/90 rounded-bl-md border border-white/6'
                }`}
              >
                {msg.content}
              </div>
              {/* Action card */}
              {msg.action_request_id && msg.action_preview && (
                <ActionCard
                  msg={msg}
                  onApprove={() => approve(msg.id, msg.action_request_id)}
                  onReject={() => reject(msg.id, msg.action_request_id)}
                />
              )}
            </div>
            {msg.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-purple to-brand-teal flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white text-xs font-bold">{initials}</span>
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex items-center gap-3 text-white/40 text-sm">
            <div className="w-8 h-8 rounded-full bg-brand-purple/15 flex items-center justify-center">
              <Bot size={16} className="text-brand-purple" />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-brand-purple animate-pulse" />
              Thinking...
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-6 lg:px-12 pb-6 pt-3">
        <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-5 py-3 max-w-3xl mx-auto">
          <input
            data-testid="chat-input"
            className="flex-1 bg-transparent text-white text-base placeholder:text-white/30 outline-none"
            placeholder="Ask me something..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || sending}
            data-testid="chat-send-btn"
            className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-purple to-brand-purple-deep flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
          >
            <Send size={16} className="text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionCard({ msg, onApprove, onReject }) {
  const preview = msg.action_preview;
  const status = msg.action_status || 'pending';
  const rows = Object.entries(preview || {}).filter(([, v]) => v !== null && v !== undefined && v !== '');

  return (
    <div className="mt-3 rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="action-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="px-2 py-1 rounded-full bg-brand-purple/15 text-brand-purple text-[10px] font-bold uppercase tracking-wider">
          AI · Action Prepared
        </span>
      </div>
      <div className="space-y-2 border-t border-white/8 pt-3">
        {rows.slice(0, 6).map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm">
            <span className="text-white/50">{k.replace(/_/g, ' ')}</span>
            <span className="text-white font-medium text-right max-w-[60%] truncate">{String(v)}</span>
          </div>
        ))}
      </div>
      {status === 'pending' ? (
        <div className="flex gap-3 mt-4">
          <button
            onClick={onReject}
            data-testid="action-reject-btn"
            className="flex-1 h-11 rounded-full border border-red-400 text-red-400 font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-400/10 transition-colors"
          >
            <X size={14} /> Reject
          </button>
          <button
            onClick={onApprove}
            data-testid="action-approve-btn"
            className="flex-1 h-11 rounded-full bg-emerald-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors shadow-lg"
          >
            <Check size={14} /> Approve
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 mt-4 py-2">
          {status === 'approved' ? <Check size={16} className="text-emerald-500" /> : <X size={16} className="text-red-400" />}
          <span className={`font-bold text-sm ${status === 'approved' ? 'text-emerald-500' : 'text-red-400'}`}>
            {status === 'approved' ? 'Approved & saved' : 'Rejected'}
          </span>
        </div>
      )}
    </div>
  );
}
